// Structured-data (JSON-LD) builders for rich results.
// Kept framework-free so the <Seo> component can inject them into <head> and
// the build-time prerenderer (react-snap) captures them into static HTML.
//
// Note on schema choice: Google's price + star-rating rich results are driven by
// `Product` (with Offer/AggregateOffer + AggregateRating). Our tours & experiences
// are priced, review-bearing catalog items (not date/time-bound happenings), so we
// model them as `Product` rather than `Event` — `Event` requires a concrete
// startDate we do not have, and inventing one would be invalid/spammy.

const BASE = 'https://flightdeals.in';
const LOGO = `${BASE}/og-default.jpg`;

export const abs = (path = '') => (`${BASE}${path || ''}`).replace(/\/+$/, '') || BASE;

// Absolute URL to a branded Open Graph share image (served by the backend).
// e.g. ogImage('tour/japan-7-day-guided-tour.png')
export const ogImage = (subpath) => `${BASE}/api/og/${subpath}`;

const clean = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' ) return;
    out[k] = v;
  });
  return out;
};

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'FlightDeals.in',
  url: BASE,
  logo: LOGO,
});

export const breadcrumb = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) =>
    clean({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.path ? abs(it.path) : undefined,
    })
  ),
});

// Priced catalog item (store product, tour, experience) -> price + rating snippet.
export const productSchema = ({
  name,
  description,
  image,
  path,
  brand,
  price,
  priceCurrency = 'INR',
  offerCount,
  rating,
  reviewCount,
  availability = 'https://schema.org/InStock',
}) => {
  const s = clean({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image,
    url: abs(path),
    brand: brand ? { '@type': 'Brand', name: brand } : undefined,
  });
  if (rating && reviewCount) {
    s.aggregateRating = clean({
      '@type': 'AggregateRating',
      ratingValue: String(rating),
      reviewCount: String(reviewCount),
    });
  }
  if (price != null && !Number.isNaN(Number(price))) {
    s.offers =
      offerCount && offerCount > 1
        ? clean({
            '@type': 'AggregateOffer',
            lowPrice: String(Math.round(Number(price))),
            priceCurrency,
            offerCount: String(offerCount),
            availability,
            url: abs(path),
          })
        : clean({
            '@type': 'Offer',
            price: String(Math.round(Number(price))),
            priceCurrency,
            availability,
            url: abs(path),
          });
  }
  return s;
};

export const faqPageSchema = (items = []) =>
  clean({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items
      .filter((it) => it && it.q && it.a)
      .map((it) => ({
        '@type': 'Question',
        name: it.q,
        acceptedAnswer: { '@type': 'Answer', text: it.a },
      })),
  });

export const personSchema = ({ name, alternateName, image, description, path, sameAs }) =>
  clean({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name,
    alternateName,
    image,
    description,
    url: abs(path),
    sameAs: sameAs && sameAs.length ? sameAs : undefined,
  });

export const articleSchema = ({
  headline,
  description,
  image,
  path,
  authorName,
  authorPath,
  datePublished,
}) =>
  clean({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    description,
    image,
    mainEntityOfPage: abs(path),
    author: authorName
      ? clean({ '@type': 'Person', name: authorName, url: authorPath ? abs(authorPath) : undefined })
      : undefined,
    publisher: {
      '@type': 'Organization',
      name: 'FlightDeals.in',
      logo: { '@type': 'ImageObject', url: LOGO },
    },
    datePublished,
  });
